import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/common/PageHeader';
import { BatchMonitoringTab } from '../../components/data-integration/BatchMonitoringTab';
import { ResourceCountsTab } from '../../components/data-integration/ResourceCountsTab';
import { SchemaDefinitionsWithMappings } from '../../components/SchemaDefinitionsWithMappings';
import { SenderProfilesTab } from '../data-integration/sender-profiles';
import { useAllFlags } from '../../hooks/useAllFlags';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

type TabName = 'schemas' | 'batches' | 'counts' | 'sender-profiles';

/**
 * Data Integration Settings Page
 * Combined interface for schema definitions, field mappings, and batch monitoring
 */
export const DataIntegrationPage: React.FC = () => {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { ld } = useAllFlags();
  const showSenderProfiles = ld['enable-sender-profiles'] ?? false;

  // Initialize activeTab from URL parameter, default to 'schemas'
  const [activeTab, setActiveTab] = useState<TabName>(() => {
    const validTabs: TabName[] = ['schemas', 'batches', 'counts', 'sender-profiles'];
    const initialTab = tab && validTabs.includes(tab as TabName) ? (tab as TabName) : 'schemas';
    return initialTab;
  });

  // Handle URL changes (browser back/forward) - sync activeTab with URL
  useEffect(() => {
    const validTabs: TabName[] = ['schemas', 'batches', 'counts', 'sender-profiles'];
    const urlTab = tab && validTabs.includes(tab as TabName) ? (tab as TabName) : 'schemas';

    setActiveTab(urlTab);
    // Mark that initial mount is complete
    isInitialMount.current = false;
  }, [tab]);

  // Helper function to handle tab navigation
  const handleTabChange = (newTab: string) => {
    const validTabs: TabName[] = ['schemas', 'batches', 'counts', 'sender-profiles'];
    if (!validTabs.includes(newTab as TabName)) {
      return;
    }

    const tabName = newTab as TabName;

    // Don't navigate if we're already on the correct tab or on initial mount
    if (tabName === activeTab || isInitialMount.current) {
      return;
    }

    const currentPath = '/dashboard/settings/data-integration';
    const newPath = tabName === 'schemas' ? currentPath : `${currentPath}/${tabName}`;

    // Update both state and URL
    setActiveTab(tabName);
    navigate(newPath, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Data Integration"
        description="Define your data structure, configure field mappings, and monitor batch uploads"
      />
      <Tabs value={activeTab} onValueChange={handleTabChange} defaultValue={activeTab}>
        <TabsList>
          <TabsTrigger value="schemas">Schema Definitions & Mappings</TabsTrigger>
          {showSenderProfiles && <TabsTrigger value="sender-profiles">Sender Profiles</TabsTrigger>}
          <TabsTrigger value="batches">Batch Uploads</TabsTrigger>
          <TabsTrigger value="counts">Resource Counts</TabsTrigger>
        </TabsList>
        <TabsContent value="schemas" className="mt-6">
          <SchemaDefinitionsWithMappings />
        </TabsContent>
        {showSenderProfiles && (
          <TabsContent value="sender-profiles" className="mt-6">
            <SenderProfilesTab />
          </TabsContent>
        )}
        <TabsContent value="batches" className="mt-6">
          <BatchMonitoringTab />
        </TabsContent>
        <TabsContent value="counts" className="mt-6">
          <ResourceCountsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};
